import React from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { AuthProvider, useAuth } from "../contexts/AuthContext"
import { ToastProvider } from "../contexts/ToastContext"
import Layout from "./layout/Layout"
import Dashboard from "./dashboard/Dashboard"
import Settings from "./settings/Settings"
import ParentDashboard from "./parent/ParentDashboard"
import AcceptInvitation from "./invitations/AcceptInvitation"
import MarketingPage from "./marketing/MarketingPage"
import CanvasTokenHelp from "./help/CanvasTokenHelp"
import TermsOfService from "./legal/TermsOfService"
import AcceptableUse from "./legal/AcceptableUse"
import PrivacyPolicy from "./legal/PrivacyPolicy"
import AiAssistantsPage from "./assistants/AiAssistantsPage"
import Support from "./support/Support"

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // Redirect to ERB login page
    window.location.href = "/users/sign_in"
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return children
}

const ParentRoute = ({ children }) => {
  const { isAuthenticated, isParent, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    window.location.href = "/users/sign_in"
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!isParent) {
    return <Navigate to="/" replace />
  }

  return children
}

const RoleBasedRedirect = () => {
  const { isParent, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (isParent) {
    return <Navigate to="/parent-dashboard" replace />
  }

  return <Dashboard />
}

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <MarketingPage />
  }

  return children
}

const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/ai-assistants" element={<AiAssistantsPage />} />
      <Route path="/welcome" element={<MarketingPage />} />
      <Route
        path="/accept-invitation/:token"
        element={<AcceptInvitation />}
      />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/legal/acceptable-use" element={<AcceptableUse />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/support" element={<Support />} />
      <Route
        path="/"
        element={
          <PublicRoute>
            <Layout />
          </PublicRoute>
        }
      >
        <Route index element={<RoleBasedRedirect />} />
        <Route path="settings" element={<Settings />} />
        <Route path="help/canvas-token" element={<CanvasTokenHelp />} />
        <Route
          path="parent-dashboard"
          element={
            <ParentRoute>
              <ParentDashboard />
            </ParentRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
